/**
 * "Workspace" is a frontend-only UI-organization concept (no backend
 * entity backs it) — which functional area of the HMIS the user is
 * currently working in, driving the Workspace Switcher (STEP 5) and,
 * later, which feature-module routes/nav items are visible by
 * default. Distinct from Role/Permission (which govern what a user
 * CAN do) — Workspace governs what they're currently LOOKING AT.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MODIFICATIONS_MODULE } from '@core/permissions/modifications-access';
import { DOWNLOAD_MODULE } from '@core/permissions/download-access';

export type WorkspaceId =
  | 'front-desk'
  | 'ip'
  | 'discharge'
  | 'mrd'
  | 'billing'
  | 'main-counter'
  | 'finance'
  | 'pharmacy'
  | 'general-stores'
  | 'laboratory'
  | 'radiology'
  | 'insurance'
  | 'reports'
  | 'modifications'
  | 'download'
  | 'administration';

export interface Workspace {
  id: WorkspaceId;
  label: string;
}

export const AVAILABLE_WORKSPACES: Workspace[] = [
  { id: 'front-desk', label: 'Front Desk' },
  /*
   * THE INPATIENT DESK — desk, 2026-08-20: "create one IP workstation… move all
   * IP related modules here."
   *
   * The IP screens were scattered by the section they happen to live in:
   * admitting under Front Office because a receptionist does it, the billing
   * workstation under Billing because it takes money. That is how the system is
   * organised, not how the WARD is: one desk admits, captures charges against
   * the stay, settles it and discharges it, and it was reaching those through
   * two different workspaces.
   *
   * Placed second, straight after Front Desk, because that is the order a stay
   * moves in — the patient is registered at the front, then admitted.
   */
  { id: 'ip', label: 'IP' },
  /*
   * DISCHARGE (owner, 2026-10-23: "need Discharge Module under sidebar") — straight after IP because that is where a stay goes
   * next: the ward advises, this desk walks it out. A SEPARATE workspace, not an IP tab, so a discharge coordinator is given the
   * discharge desk without the IP billing desk, and so Administration → Roles can grant it like any other module.
   */
  { id: 'discharge', label: 'Discharge' },
  /*
   * MRD (owner, 2026-11-02: "need new module named MRD") — the Medical Records Department, straight after Discharge because that is where a
   * finished stay's file goes next: the ward hands it over, this desk takes it in, codes it and keeps the registers. A SEPARATE workspace,
   * gated on its own role module (`MRD`), so the records room is granted apart from the discharge desk and the ward.
   */
  { id: 'mrd', label: 'MRD' },
  { id: 'billing', label: 'Billing' },
  /*
   * THE MOTHER COUNTER — desk, 2026-08-28: "this should not be Finance… keep
   * separate as a Main Counter."
   *
   * Placed straight after Billing because that is where the money comes FROM:
   * the counters take it, this desk receives, reconciles and settles it. It is
   * deliberately NOT under Finance — overseeing cash custody does not require
   * handing somebody the general ledger, which is the whole point of the split.
   *
   * ITS ABSENCE HERE IS WHAT EMPTIED USER 0007'S SIDEBAR. With no entry, the
   * Main Counter Supervisor matched no workspace at all, and the "never return
   * an empty list" fallback below handed them `front-desk` — the one workspace
   * carrying a flat rail — every row of which needs FRONT_OFFICE, which this
   * role does not hold. Menu: empty. Header: "Front Desk". And with switching
   * withheld from non-administrators, no way out of it.
   */
  { id: 'main-counter', label: 'Main Counter' },
  { id: 'pharmacy', label: 'Pharmacy' },
  /*
   * THE GENERAL STORE — desk, 2026-09-06: "add general stores in workstation
   * changeover places".
   *
   * Straight after Pharmacy because that is the adjacency in the building: both
   * hold stock, both receive from vendors, and at this hospital the same person
   * often works one then the other in a shift. It is a SEPARATE workspace and
   * not a Pharmacy tab for the reason the two stock screens are separate — only
   * one of them may be dispensed from, and a storekeeper must never be one
   * mis-click from the drug counter.
   */
  { id: 'general-stores', label: 'General Stores' },
  { id: 'laboratory', label: 'Laboratory' },
  { id: 'radiology', label: 'Radiology' },
  /*
   * THE INSURANCE DESK — desk, 2026-09-27: "insurance workstation is missing".
   *
   * The Insurance section (ADR-098: pre-auth, claims, receipts, receivables,
   * shortfalls, plans, insurers) was deployed 2026-09-26 and existed only in the
   * full collapsible tree an administrator sees. The switcher is the way into a
   * desk, and no workspace listed this one, so it could not be picked.
   *
   * Placed before Finance because it is the desk that chases money owed by third
   * parties, worked alongside the counters; the ledger it posts to is Finance's.
   */
  { id: 'insurance', label: 'Insurance' },
  // Finance sits after the counters on purpose: the desks come first because
  // that is who switches workspace all day, and the ledgers are a separate job.
  { id: 'finance', label: 'Finance' },
  /*
   * REPORTS & ANALYTICS — desk, 2026-09-08: "in this selection of workplace...
   * report&analysis is not available add".
   *
   * The section has existed in the registry since 2026-09-04 (`group('reports',
   * …, 'REPORTS')`) and carries six leaves, three of them the standalone
   * `/reports` route. It was reachable only from the full collapsible tree —
   * which is to say, only by an administrator. Everyone else holds a FLAT rail
   * scoped to their own workspace, and no workspace listed this section, so a
   * role granted the REPORTS module had the module, the permission and the
   * routes, and no way to open any of them.
   *
   * A WORKSPACE, NOT A ROW ON EVERY RAIL. Reports read ACROSS desks — the
   * registration report answers a question the front desk asks about the
   * appointment book, the collection report spans OP and IP — so they belong to
   * no one counter, which is exactly the argument that gave them their own
   * route. Adding a Reports row to each rail would be the same list six times,
   * each one a different subset by accident.
   *
   * LAST BEFORE ADMIN, because reading the day is what happens after it: the
   * desks that switch workspace all day come first, and this is the one people
   * open once, at the end.
   */
  { id: 'reports', label: 'Reports & Analytics' },
  /*
   * MODIFICATIONS — desk, 2026-10-07: "need new Module called Modifications… only approve flow should act in this module… show
   * only who have approve access under Administration → Approver. Even super admin and administrator can't see this module."
   *
   * Where an APPROVER decides the corrections the front desk requested and a verifier checked (OP and IP). It is NOT a role
   * module: see `WORKSPACE_MODULE_CODE.modifications` and `core/permissions/modifications-access.ts` for why, and for the one place
   * the code is granted.
   *
   * Just before Admin, after Reports: it is a place people go to when something is waiting, not one they switch to all day.
   */
  { id: 'modifications', label: 'Modifications' },
  // Offered only to a holder of `data-export:read` — never through a role module (see `core/permissions/download-access.ts`).
  { id: 'download', label: 'Download' },
  // 'Admin', not 'Administration' — the switcher is a short list of places, and
  // the id stays `administration` so nothing persisted or gated has to move.
  { id: 'administration', label: 'Admin' },
];

/**
 * The module grant each workspace stands for. A workspace is an entry point
 * into a functional area, so offering one the user has no module for hands
 * them a switch to somewhere they cannot go — a receptionist was being shown
 * Clinical, Pharmacy, Laboratory and Administration in the switcher.
 *
 * Deliberately reuses `RoleModule`'s own codes rather than inventing a second
 * gate: the switcher and the sidebar then agree by construction, because both
 * read the same `moduleCodes` claim.
 */
export const WORKSPACE_MODULE_CODE: Record<WorkspaceId, string> = {
  'front-desk': 'FRONT_OFFICE',
  // `CLINICAL_IPD`, not `BILLING`: this workspace is the inpatient stay, and a
  // role granted inpatient care is the one that should be offered it. Reusing an
  // existing RoleModule rather than inventing a code keeps the switcher and the
  // sidebar agreeing by construction — both read the same `moduleCodes` claim.
  ip: 'CLINICAL_IPD',
  // Its own module code (NOT CLINICAL_IPD): one code doing two jobs cannot be granted apart.
  discharge: 'DISCHARGE',
  mrd: 'MRD',
  billing: 'BILLING',
  'main-counter': 'MAIN_COUNTER',
  finance: 'BILLING_FINANCE',
  pharmacy: 'PHARMACY',
  /*
   * `MATERIALS`, the module code the old placeholder Materials section left
   * behind when its empty leaves were deleted in 2026-08. Reused rather than
   * invented, so the switcher and the sidebar gate on the SAME claim — the
   * General Stores section in the registry already carries it.
   *
   * PROVISIONING: `MATERIALS` sits on SuperAdmin and System Administrator only.
   * A storekeeper role needs it in `role_modules` or this workspace is not
   * offered to them at all, however many stores permissions they hold.
   */
  'general-stores': 'MATERIALS',
  laboratory: 'LABORATORY',
  radiology: 'RADIOLOGY',
  /*
   * `INSURANCE`, the code the registry's own `group('insurance', …)` gates on.
   * Reused so the switcher and the sidebar agree by construction.
   *
   * PROVISIONING: the code sits on SuperAdmin and System Administrator only. The
   * Insurance Manager / Insurance Executive roles hold no `role_modules` rows, so
   * they are not offered this workspace until a desk decision grants them it.
   */
  insurance: 'INSURANCE',
  /*
   * `REPORTS`, the code the registry's own `group('reports', …)` already gates
   * on. Reused rather than invented so the switcher and the sidebar agree by
   * construction — both read the same `moduleCodes` claim.
   *
   * PROVISIONING: a role needs `REPORTS` in `role_modules` or this workspace is
   * not offered, however many `analytics:report:view` grants it holds — the
   * same two-step every other workspace requires.
   */
  reports: 'REPORTS',
  /*
   * `MODIFICATIONS` is deliberately NOT one of `NAV_MODULE_CODES`: no role can be granted it, and the SuperAdmin / System
   * Administrator, who hold every real code, do not hold this one. `useEffectiveModuleCodes` adds it for a user the server says
   * is a selected correction approver, and only for them — see `core/permissions/modifications-access.ts`.
   */
  modifications: MODIFICATIONS_MODULE,
  /*
   * `DOWNLOAD` is deliberately NOT one of `NAV_MODULE_CODES` either: no role can be granted it, and the SuperAdmin / System Administrator
   * do not get it from holding every real code. `useEffectiveModuleCodes` adds it for a user whose token carries `data-export:read`.
   */
  download: DOWNLOAD_MODULE,
  administration: 'ADMINISTRATION',
};

/**
 * WHERE A USER GOES WHEN THEY MATCH NO WORKSPACE AT ALL.
 *
 * `administration` is used for its NARROWING, not for its name: it has no
 * `WORKSPACE_SECTIONS` entry and no flat rail, so it is the one workspace that
 * hides nothing and lets the sidebar's own permission and module gating decide
 * what the user sees. The label says "All modules" rather than "Admin" because
 * this is not an administrator — it is somebody whose role has no workspace yet.
 *
 * IT GRANTS NOTHING. A workspace only ever decides what is OFFERED; every row
 * still passes `hasAccess` in `Sidebar.tsx`, so landing here can show a user
 * only what their own modules and permissions already allow. `canSwitchWorkspace`
 * likewise reads the module claim, never this id.
 */
const FALLBACK_WORKSPACE: Workspace = { id: 'administration', label: 'All modules' };

/**
 * The workspaces this user may actually switch to.
 *
 * Never returns an empty list: a user with no matching module still needs a
 * label in the header and a defined `currentWorkspaceId`.
 *
 * THE STAND-IN MUST NARROW NOTHING, and that is the correction here. This used
 * to fall back to `AVAILABLE_WORKSPACES[0]` — Front Desk — which is the single
 * MOST narrowing workspace in the list: it is the only one carrying a flat
 * rail, and every row of that rail needs FRONT_OFFICE. So an unmatched user was
 * shown seven rows they had no module for, leaving a blank menu, while
 * `WorkspaceSwitcher`'s snap-back effect sat idle because the fabricated
 * Front Desk entry satisfied its `current` lookup. Falling back to the
 * non-narrowing workspace fails in the safe direction instead: a sidebar with
 * everything the user actually holds, rather than nothing at all.
 */
export function availableWorkspacesFor(moduleCodes: string[]): Workspace[] {
  const allowed = AVAILABLE_WORKSPACES.filter((workspace) =>
    moduleCodes.includes(WORKSPACE_MODULE_CODE[workspace.id]),
  );
  return allowed.length > 0 ? allowed : [FALLBACK_WORKSPACE];
}

/**
 * Whether this user may CHANGE workspace at all.
 *
 * ── ANYONE HOLDING MORE THAN ONE WORKSPACE MAY MOVE BETWEEN THEM ────────────
 * (desk, 2026-09-08: user 2087 "nothing is showing... we have given access
 * main counter".)
 *
 * This read `moduleCodes.includes('ADMINISTRATION')` and nothing else, and the
 * reasoning was sound as far as it went: a workspace is the functional area a
 * role puts somebody in, not a preference, and a pharmacist OFFERED "Front
 * Desk" reads it as an invitation to a job that is not theirs.
 *
 * But it stranded the case it did not anticipate. User 2087 was deliberately
 * granted Front Desk, Main Counter and IP Billing. All three appeared in
 * `availableWorkspacesFor`; none of them was reachable, because the control
 * that moves between them was withheld. She opened on the workspace her
 * localStorage happened to hold and had no way out of it — the exact
 * "pinned to a workspace they cannot leave" failure the old comment named as
 * the reason ADMINISTRATION keeps the control, applied to somebody who was
 * never given it.
 *
 * THE ORIGINAL INTENT IS FULLY PRESERVED, because it was never this function
 * that enforced it. `availableWorkspacesFor` decides what EXISTS for a user,
 * and it lists only workspaces whose module they actually hold — so a
 * pharmacist is still never offered Front Desk, with or without this. What
 * changes is only that somebody holding two or more of their own workspaces can
 * reach the second one. A user with exactly one still sees a plain label:
 * `availableWorkspacesFor` returns a single entry and `WorkspaceSwitcher`
 * refuses to render a dropdown onto one item.
 *
 * ADMINISTRATION IS KEPT as an explicit clause rather than left to fall out of
 * the count. An administrator crosses every module by definition, and their
 * ability to switch should not depend on how many workspaces happen to be
 * defined today.
 *
 * Gating the CONTROL, never the ACCESS: permission and module gating in the
 * sidebar is untouched, so switching can only ever reach rows the user could
 * already open.
 */
export function canSwitchWorkspace(moduleCodes: string[]): boolean {
  return moduleCodes.includes('ADMINISTRATION') || availableWorkspacesFor(moduleCodes).length > 1;
}

interface WorkspaceState {
  currentWorkspaceId: WorkspaceId;
  setWorkspace: (id: WorkspaceId) => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set) => ({
      currentWorkspaceId: 'front-desk',
      setWorkspace: (id) => set({ currentWorkspaceId: id }),
    }),
    { name: 'nexao-workspace' },
  ),
);
